import { useNavigate, Link, useLocation } from "react-router";
import { Text, useI18n } from "./i18n";
import { LanguagePicker } from "./LanguagePicker";
import { Notification } from "./Notification";
import { Logo } from "./Logo";
import { EventCountdown } from "./EventCountown";
import { useToken } from "./TokenProvider";
import { useRsvp } from "../api";
import React, { useEffect, useRef, useState } from "react";
import bg from "../assets/bg.webp";
import bgPaper from "../assets/bgPaper.webp";
import { MapPinIcon } from "@heroicons/react/24/outline";
import { useIsVisible } from "./useIsVisible";
import {
  getHomeFontClass,
  getPrettyPrimaryFontClass,
  getPrettySecondaryFontClass,
  getStoryFontClass,
} from "./fonts";
import { Language } from "../../model/rsvp";
import img1 from "../assets/gallery/1.webp";
import img2 from "../assets/gallery/2.webp";
import img3 from "../assets/gallery/3.webp";
import img4 from "../assets/gallery/4.webp";
import img5 from "../assets/gallery/5.webp";

export function Home() {
  const i18n = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const tokenContext = useToken();
  const rsvp = useRsvp(tokenContext.token);
  const [noscroll, setNoscroll] = useState(false);

  const contentRef = useRef<HTMLElement>(null);
  const homeRef = useRef<HTMLElement>(null);
  const storyRef = useRef<HTMLElement>(null);
  const tidbitsRef = useRef<HTMLElement>(null);
  const scheduleRef = useRef<HTMLElement>(null);
  const faqRef = useRef<HTMLElement>(null);
  const registryRef = useRef<HTMLElement>(null);

  const isHomeRefVisible = useIsVisible(homeRef, contentRef);
  const isStoryRefVisible = useIsVisible(storyRef, contentRef);
  const isTidbitsRefVisible = useIsVisible(tidbitsRef, contentRef);
  const isScheduleRefVisible = useIsVisible(scheduleRef, contentRef);
  const isFaqRefVisible = useIsVisible(faqRef, contentRef);
  const isRegistryRefVisible = useIsVisible(registryRef, contentRef);

  useEffect(() => {
    if (noscroll) {
      return;
    }

    if (location.pathname === "/story" && storyRef.current) {
      storyRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    } else if (location.pathname === "/tidbits" && tidbitsRef.current) {
      tidbitsRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    } else if (location.pathname === "/schedule" && scheduleRef.current) {
      scheduleRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    } else if (location.pathname === "/faq" && faqRef.current) {
      faqRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    } else if (location.pathname === "/registry" && registryRef.current) {
      registryRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    } else if (location.pathname === "/" && homeRef.current) {
      homeRef.current.scrollIntoView({
        behavior: location.state?.scroll === "smooth" ? "smooth" : "instant",
      });
    }
  }, [location, navigate, noscroll]);

  useEffect(() => {
    if (isHomeRefVisible) {
      if (location.pathname !== "/") {
        setNoscroll(true);
        navigate("/", { replace: true });
      }
    } else if (isStoryRefVisible) {
      if (location.pathname !== "/story") {
        setNoscroll(true);
        navigate("/story", { replace: true });
      }
    } else if (isTidbitsRefVisible) {
      if (location.pathname !== "/tidbits") {
        setNoscroll(true);
        navigate("/tidbits", { replace: true });
      }
    } else if (isScheduleRefVisible) {
      if (location.pathname !== "/schedule") {
        setNoscroll(true);
        navigate("/schedule", { replace: true });
      }
    } else if (isFaqRefVisible) {
      if (location.pathname !== "/faq") {
        setNoscroll(true);
        navigate("/faq", { replace: true });
      }
    } else if (isRegistryRefVisible) {
      if (location.pathname !== "/registry") {
        setNoscroll(true);
        navigate("/registry", { replace: true });
      }
    }
  }, [
    navigate,
    location,
    isHomeRefVisible,
    isStoryRefVisible,
    isTidbitsRefVisible,
    isScheduleRefVisible,
    isFaqRefVisible,
    isRegistryRefVisible,
  ]);

  const hasPartnerFirstname = rsvp.data?.guests.partner?.firstName;
  const isAttending: boolean | null = rsvp.data?.guests.confirmed ?? null;

  return (
    <>
      <div
        className={`h-screen flex flex-col lg:flex-row ${getHomeFontClass(
          i18n.language
        )}`}
      >
        <Notification />
        <ImageSection
          className="hidden lg:flex"
          language={i18n.language}
          pathname={location.pathname}
        />
        <section
          className="bg-stone-200 flex flex-1 lg:flex-2 overflow-hidden"
          ref={contentRef}
        >
          <main className="flex-1 overflow-y-scroll relative text-primary">
            <section
              style={{
                backgroundImage: `url(${bg})`,
              }}
              className="min-h-screen h-fit lg:h-screen flex flex-col justify-center items-center bg-no-repeat bg-cover"
              ref={homeRef}
            >
              <LanguagePicker className="lg:visible absolute top-3 right-3" />
              <Logo />
              <section className="mt-6">
                <EventCountdown />
              </section>
              {rsvp.data?.guests && (
                <section className="flex mt-10">
                  <p>
                    <span>
                      <Text id="home.hello" />
                      &nbsp;
                    </span>
                    <span>{rsvp.data?.guests.primary.firstName}</span>
                    {hasPartnerFirstname && <span>&nbsp;&&nbsp;</span>}
                    {hasPartnerFirstname && (
                      <span>{rsvp.data.guests.partner?.firstName}.&nbsp;</span>
                    )}
                    {!hasPartnerFirstname && <span>.&nbsp;</span>}
                  </p>
                  <Link
                    to="/login"
                    className="font-medium text-primary underline hover:text-primary-hover transition-colors"
                    onClick={() => tokenContext.setToken(null)}
                  >
                    <Text
                      id={
                        hasPartnerFirstname
                          ? "home.notYou.plural"
                          : "home.notYou.singular"
                      }
                    />
                  </Link>
                </section>
              )}
              <button
                type="button"
                className="cursor-pointer border-2 border-solid border-primary rounded-md bg-transparent mt-3 px-6.5 py-4.5 font-semibold text-primary shadow-xs hover:text-primary-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary transition-colors"
                onClick={() => {
                  navigate("/rsvp");
                }}
              >
                <Text
                  id={
                    isAttending === true
                      ? rsvp.data?.guests.partner
                        ? "home.attending.yes.plural"
                        : "home.attending.yes.singluar"
                      : isAttending === false
                      ? "home.attending.no"
                      : "home.rsvp"
                  }
                />
              </button>
              <Link
                to={"/story"}
                replace={true}
                state={{ scroll: "smooth" }}
                onClick={(e) => {
                  e.preventDefault();
                  if (storyRef.current) {
                    storyRef.current.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="mt-12"
              >
                <p>
                  <Text id="home.viewDetails" />
                </p>
                <div className="text-center mt-8 mb-8">
                  <svg
                    aria-hidden="true"
                    className="m-auto h-4 animate-attention"
                    viewBox="0 0 66 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      opacity="0.8"
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M30.294 14.1a10 10 0 005.882 0l27.905-8.586a2.5 2.5 0 00-1.47-4.779L36.176 8.87a10 10 0 01-5.882 0L3.86.735a2.5 2.5 0 00-1.47 4.78L30.293 14.1z"
                      fill="currentColor"
                    ></path>
                  </svg>
                </div>
              </Link>
            </section>
            <img
              alt="Bowon & Robert"
              className="lg:hidden w-[100%] object-cover"
              src={img1}
            />
            <section
              style={{
                backgroundImage: `url(${bgPaper})`,
              }}
              className={`bg-cover px-6 py-20 sm:py-32 lg:px-8 ${getStoryFontClass(
                i18n.language
              )}`}
              ref={storyRef}
            >
              <div className="mx-auto max-w-sm">
                <h2
                  className={`text-5xl text-center sm:text-6xl ${getPrettySecondaryFontClass(
                    i18n.language
                  )}`}
                >
                  <Text id="home.story.title" />
                </h2>
                <p className="mt-8 text-lg text-pretty">
                  <Text id="home.story.content_1" />
                </p>
                <p className="mt-8 text-lg text-pretty">
                  <Text id="home.story.content_2" />
                </p>
                <p className="mt-8 text-lg text-pretty">
                  <Text id="home.story.content_3" />
                </p>
                <p className="mt-8 text-lg text-pretty">
                  <Text id="home.story.content_4" />
                </p>
              </div>
            </section>
            <img
              alt="Bowon & Robert"
              className="lg:hidden w-[100%] object-cover"
              src={img2}
            />
            <section
              style={{
                backgroundImage: `url(${bgPaper})`,
              }}
              className={`bg-cover px-6 py-20 sm:py-32 lg:px-8 ${getStoryFontClass(
                i18n.language
              )}`}
              ref={tidbitsRef}
            >
              <div className="mx-auto max-w-sm">
                <h2
                  className={`text-5xl text-center sm:text-6xl ${getPrettySecondaryFontClass(
                    i18n.language
                  )}`}
                >
                  <Text id="home.tidbits.title" />
                </h2>
                <dl className="space-y-16 grid grid-cols-1 sm:space-y-0 sm:gap-y-16 mt-14">
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_1" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_1" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_2" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_2" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_3" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_3" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_4" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_4" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_5" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_5" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_6" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_6" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_7" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_7" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.tidbits.question_8" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.tidbits.answer_8" />
                    </dd>
                  </div>
                </dl>
              </div>
            </section>
            <img
              alt="Bowon & Robert"
              className="lg:hidden w-[100%] object-cover"
              src={img3}
            />
            <section
              style={{
                backgroundImage: `url(${bgPaper})`,
              }}
              className="bg-cover px-6 py-20 sm:py-32 lg:px-8"
              ref={scheduleRef}
            >
              <div className="mx-auto max-w-sm">
                <h2
                  className={`text-5xl text-center sm:text-6xl ${getPrettySecondaryFontClass(
                    i18n.language
                  )}`}
                >
                  <Text id="home.schedule.title" />
                </h2>
                <div className="mt-8 border-b border-gray-900/10 pb-3">
                  <p className="text-center text-xl text-pretty">
                    <Text id="home.schedule.saturday.event1.day" />
                  </p>
                </div>
                <p className="text-center font-semibold text-xl text-pretty mt-12">
                  <Text id="home.schedule.saturday.event1.title" />
                </p>
                <p className="text-center font-medium text-xl text-pretty text-primary-disabled mt-2">
                  <Text id="home.schedule.saturday.event1.time" />
                </p>
                <p className="text-lg text-pretty mt-12">
                  <Text id="home.schedule.saturday.event1.description" />
                </p>
                <a
                  href="https://maps.app.goo.gl/ygQA2dsz2pHtmFYN6"
                  className="flex items-baseline text-lg text-secondary underline hover:text-secondary-hover transition-colors mt-6"
                  target="_blank"
                >
                  <MapPinIcon className="size-6 mr-2" />
                  <Text id="home.schedule.saturday.event1.location" />
                </a>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d4307.804225220312!2d20.233623577473608!3d49.276685771177604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473e1ef5884b4d81%3A0xef60446f4da89d5d!2sMedved%C3%AD%20brloh%20Strachan!5e1!3m2!1sen!2sus!4v1742765433383!5m2!1sen!2sus"
                  allowFullScreen={undefined}
                  loading="lazy"
                  className="border-0 box-border rounder-md w-full h-[320px] mt-4"
                  referrerPolicy="no-referrer-when-downgrade"
                ></iframe>

                <p className="text-center font-semibold text-xl text-pretty mt-12">
                  <Text id="home.schedule.saturday.event2.title" />
                </p>
                <p className="text-center font-medium text-xl text-pretty text-primary-disabled mt-2">
                  <Text id="home.schedule.saturday.event2.time" />
                </p>
                <p className="text-lg text-pretty mt-12">
                  <Text id="home.schedule.saturday.event2.description" />
                </p>
                <a
                  href="https://maps.app.goo.gl/ygQA2dsz2pHtmFYN6"
                  className="flex items-baseline text-lg text-secondary underline hover:text-secondary-hover transition-colors mt-6"
                  target="_blank"
                >
                  <MapPinIcon className="size-6 mr-2" />
                  <Text id="home.schedule.saturday.event1.location" />
                </a>

                <div className="mt-14 border-b border-gray-900/10 pb-3">
                  <p className="text-center text-xl text-pretty">
                    <Text id="home.schedule.toBeAnnounced" />
                  </p>
                </div>
                <p className="font-semibold text-xl text-pretty mt-10">
                  <Text id="home.schedule.toBeAnnounced.event1.title" />
                </p>
                <p className="text-lg text-pretty mt-6">
                  <Text id="home.schedule.toBeAnnounced.event1.description" />
                </p>
              </div>
            </section>
            <img
              alt="Bowon & Robert"
              className="lg:hidden w-[100%] object-cover"
              src={img4}
            />
            <section
              style={{
                backgroundImage: `url(${bgPaper})`,
              }}
              className="bg-cover px-6 py-20 sm:py-32 lg:px-8"
              ref={faqRef}
            >
              <div className="mx-auto max-w-sm">
                <h2
                  className={`text-5xl text-center sm:text-6xl ${getPrettySecondaryFontClass(
                    i18n.language
                  )}`}
                >
                  <Text id="home.faq.title" />
                </h2>
                <dl className="space-y-16 grid grid-cols-1 sm:space-y-0 sm:gap-y-16 mt-14">
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.faq.question_1" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.faq.answer_1" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.faq.question_2" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.faq.answer_2" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.faq.question_3" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.faq.answer_3" />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-lg font-semibold">
                      <Text id="home.faq.question_4" />
                    </dt>
                    <dd className="mt-2 text-lg">
                      <Text id="home.faq.answer_4" />
                    </dd>
                  </div>
                </dl>
              </div>
            </section>
            <img
              alt="Bowon & Robert"
              className="lg:hidden w-[100%] object-cover"
              src={img5}
            />
            <section
              style={{
                backgroundImage: `url(${bgPaper})`,
              }}
              className="bg-cover px-6 py-20 sm:py-32 lg:px-8"
              ref={registryRef}
            >
              <div className="mx-auto max-w-sm">
                <h2
                  className={`text-5xl text-center sm:text-6xl ${getPrettySecondaryFontClass(
                    i18n.language
                  )}`}
                >
                  <Text id="home.registry.title" />
                </h2>
                <p className="mt-8 text-center text-lg text-pretty">
                  <Text id="home.registry.content" />
                </p>
              </div>
            </section>
          </main>
        </section>
      </div>
    </>
  );
}

const ImageSection: React.FC<{
  className: HTMLElement["className"];
  language: Language;
  pathname: string;
}> = ({ className, language, pathname }) => {
  const locationMap: Record<string, string> = {
    "/": img1,
    "/story": img2,
    "/tidbits": img3,
    "/schedule": img4,
    "/faq": img5,
    "/registry": img5,
  };

  const currentItem = locationMap[pathname] ?? img1;
  const items = [img1, img2, img3, img4, img5];

  return (
    <section className={`text-primary flex-1 lg:flex-3 ${className}`}>
      <div className="h-screen lg:h-full w-[100%] relative">
        <div className="h-[100%] min-w-[100%] absolute">
          {items.map((item) => {
            return (
              <img
                key={item}
                alt="Bowon & Robert"
                className={`h-[100%] w-[100%] absolute object-cover transition-opacity ease-in-out duration-400 ${
                  currentItem === item ? "opacity-100" : "opacity-0"
                }`}
                src={item}
              />
            );
          })}
        </div>
      </div>
      <h1
        className={`z-10 text-2xl lg:text-5xl bg-transparent absolute top-10 left-10 transition-colors ease-in-out duration-400 text-pretty ${getPrettyPrimaryFontClass(
          language
        )} ${currentItem === img5 ? "text-secondary" : "text-blue-200"}`}
      >
        <Text id="home.title" />
      </h1>
    </section>
  );
};
